/**
 * ImmunityBlockBanner.tsx — WEB.4.UI.17
 *
 * Transient top-center notification fired when a destruction-class attack
 * (Mercenaries Destroy / Climate_Protest Decom / Smog StealFacility / Spy
 * StealCard) is intercepted by the defender's Immunity. The attacker's card
 * has still been consumed (paid the play cost) and the defender's shield is
 * decremented by one charge; this banner gives both players visible feedback.
 *
 * Subscribes to `lastImmunityBlock`; auto-dismisses after 4.5s. Click to
 * dismiss immediately.
 */

import type React from "react";
import { useEffect } from "react";
import { useI18n } from "../../i18n/useI18n.js";
import { useGameStore } from "../../store/game-store.js";
import styles from "./ImmunityBlockBanner.module.css";

function effectKindKey(kind: string): string {
  switch (kind) {
    case "Destroy":
      return "immunityBlock.destructionBlocked";
    case "Decommission":
      return "immunityBlock.decommissionBlocked";
    case "StealFacility":
      return "immunityBlock.facilityTakeoverBlocked";
    case "StealCard":
      return "immunityBlock.cardTheftBlocked";
    default:
      return "immunityBlock.attackBlocked";
  }
}

export function ImmunityBlockBanner(): React.ReactElement | null {
  const { t } = useI18n();
  const lastImmunityBlock = useGameStore((s) => s.lastImmunityBlock);
  const dismissImmunityBlock = useGameStore((s) => s.dismissImmunityBlock);
  // WEB.4.TM (P0.4): stack below ThresholdEventBanner when both are active so
  // they don't overlap at top-center. Each banner is ~140px tall + gap.
  const thresholdActive = useGameStore((s) => s.lastThresholdEvent !== null);
  const topOffsetPx = thresholdActive ? 240 : 80;

  useEffect(() => {
    if (lastImmunityBlock === null) return;
    const t = window.setTimeout(dismissImmunityBlock, 4500);
    return () => window.clearTimeout(t);
  }, [lastImmunityBlock, dismissImmunityBlock]);

  if (lastImmunityBlock === null) return null;

  return (
    <button
      type="button"
      className={styles.banner}
      style={{ top: `${topOffsetPx}px` }}
      onClick={dismissImmunityBlock}
      aria-label="Dismiss immunity-block notification"
      data-testid="immunity-block-banner"
      data-effect-kind={lastImmunityBlock.effectKind}
      data-defender={lastImmunityBlock.defenderSeat}
      data-attacker={lastImmunityBlock.attackerSeat}
      data-stacked={thresholdActive ? "true" : "false"}
    >
      <div className={styles.shieldIcon}>🛡</div>
      <div className={styles.body}>
        <div className={styles.title}>{t(effectKindKey(lastImmunityBlock.effectKind))}</div>
        <div className={styles.detail}>
          Seat {lastImmunityBlock.defenderSeat + 1} blocked Seat{" "}
          {lastImmunityBlock.attackerSeat + 1}'s {lastImmunityBlock.sourceCardName}
        </div>
        <div className={styles.hint}>Click to dismiss</div>
      </div>
    </button>
  );
}
